/**
 * AI Insights Summary Card — neat, clean card for call/lead AI summary.
 * Shows: lead avatar (initials), lead name, optional subtitle, AI badge with star, summary, optional tags.
 * Use as Tooltip title or inside a Popover.
 */

import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { LEAD_THEME as LEAD_T } from '../../utils/lead_helper_v2';

const CARD_WIDTH = 340;

/**
 * Get short name / initials from full name (e.g. "Kritika Malhotra" → "KM").
 */
export function getLeadInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Get summary text from call (insights.summary or general_insights.summary).
 */
export function getSummaryFromCall(call) {
  if (!call) return '';
  const raw = call?.insights?.summary ?? call?.general_insights?.summary ?? '';
  return typeof raw === 'string' ? raw.trim() : String(raw).trim();
}

export default function AIInsightsSummaryCard({
  call,
  leadName: leadNameProp,
  summary: summaryProp,
  subtitle,
  tags = [],
}) {
  const leadName = leadNameProp ?? call?.lead?.name ?? 'Unknown';
  const summary = summaryProp ?? getSummaryFromCall(call);
  const initials = getLeadInitials(leadName);

  if (!summary) return null;

  return (
    <Box
      sx={{
        width: CARD_WIDTH,
        maxWidth: '90vw',
        p: 1.5,
      }}
    >
      {/* Header: avatar + name + optional subtitle + AI badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          mb: 1.25,
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: LEAD_T.green,
            color: 'white',
            fontSize: '0.8125rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: LEAD_T.ink2,
              fontSize: '0.875rem',
              lineHeight: 1.3,
            }}
          >
            {leadName}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: LEAD_T.muted,
                fontSize: '0.7rem',
                mt: 0.25,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {/* AI badge with star */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.35,
            borderRadius: 99,
            bgcolor: LEAD_T.greenLt,
            border: '1px solid',
            borderColor: LEAD_T.green,
            flexShrink: 0,
          }}
        >
          <StarIcon sx={{ fontSize: 12, color: LEAD_T.green }} />
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: LEAD_T.green,
              letterSpacing: '0.02em',
            }}
          >
            AI
          </Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Typography
        variant="body2"
        sx={{
          color: LEAD_T.ink2,
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {summary}
      </Typography>

      {/* Optional tags */}
      {Array.isArray(tags) && tags.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1.25,
            pt: 1,
            borderTop: `1px solid ${LEAD_T.borderL}`,
          }}
        >
          {tags.slice(0, 6).map((tag, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 99,
                bgcolor: LEAD_T.greenLt,
                border: '1px solid',
                borderColor: LEAD_T.border,
                fontSize: 10,
                fontWeight: 600,
                color: LEAD_T.ink2,
              }}
            >
              {typeof tag === 'string' ? tag : tag?.label ?? tag}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export const AI_SUMMARY_CARD_WIDTH = CARD_WIDTH;
